import { NavLink, Link } from "react-router-dom";
import {
  PlusCircle,
  BookOpen,
  GraduationCap,
  Compass,
  BarChart3,
  LogOut,
  RotateCcw,
  Sparkles,
  CreditCard,
  Flame,
  Trophy,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { auth } from "@/lib/firebase";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { getStreakData, getWords } from "@/hooks/useFirestore";
import { StreakData } from "@/types";
import { ProBadge } from "@/components/ui/pro-badge";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const navItems = [
  { to: "/add", label: "Add Words", icon: PlusCircle, walkthroughId: "nav-add-words" },
  { to: "/words", label: "My Words", icon: BookOpen, walkthroughId: "nav-my-words" },
  { to: "/practice", label: "Practice", icon: GraduationCap, walkthroughId: "nav-practice" },
  { to: "/scenarios", label: "Scenarios", icon: Compass, walkthroughId: "nav-scenarios" },
];

// TODO: Russian support hidden — re-enable by adding "he-ru" and "ru-he" entries
const DIRECTION_LABELS: Record<string, string> = {
  "he-en": "Hebrew \u2192 English",
  "en-he": "English \u2192 Hebrew",
};

export function Navigation() {
  const { user, signOut } = useAuth();
  // TODO: Russian support hidden — re-enable by destructuring setNativeLanguage
  const {
    preferences,
    setDefaultTranslationDirection,
    setAutoCategories,
    setGender,
    setWalkthroughCompleted,
  } = useUserPreferences();
  const { isPro, openUpgradePrompt } = useSubscription();
  const [streak, setStreak] = useState<StreakData | null>(null);
  const [masteredCount, setMasteredCount] = useState(0);

  useEffect(() => {
    if (!user) { setStreak(null); setMasteredCount(0); return; }
    getStreakData(user.uid).then(setStreak).catch(console.error);
    // Count mastered words (FSRS state 2 = Review = graduated)
    getWords(user.uid).then((words) => {
      const mastered = words.filter((w) => (w.reviewStats.state ?? 0) === 2).length;
      setMasteredCount(mastered);
    }).catch(console.error);
  }, [user]);

  const initials = user?.displayName
    ? user.displayName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || "?";

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="flex h-14 items-center justify-between">
          {/* Logo */}
          <Link to="/landing" className="flex items-center gap-2 flex-shrink-0">
            <img src="/logo.svg" alt="Shoresh" className="h-8 w-8" />
            <span className="font-semibold text-lg">Shoresh</span>
          </Link>

          {/* Navigation Links */}
          <nav className="hidden md:flex items-center gap-1 ml-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-walkthrough={item.walkthroughId}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? "bg-secondary text-secondary-foreground"
                      : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Streak + Mastered Badges — visible on all screen sizes, clickable to stats */}
          <Link to="/stats" data-walkthrough="nav-stats" className="flex items-center gap-2 flex-shrink-0 hover:opacity-80 transition-opacity">
            {streak && streak.currentStreak > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-50 text-orange-600 text-sm font-medium">
                <Flame className="h-4 w-4" />
                {streak.currentStreak}
              </div>
            )}
            {masteredCount > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 text-green-700 text-sm font-medium">
                <Trophy className="h-4 w-4" />
                <span className="hidden sm:inline">{masteredCount} mastered</span>
                <span className="sm:hidden">{masteredCount}</span>
              </div>
            )}
          </Link>

          {/* User Menu */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger className="outline-none flex-shrink-0">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt=""
                    className="h-8 w-8 rounded-full ring-2 ring-transparent hover:ring-primary/20 transition-all object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium hover:opacity-90 transition-opacity">
                    {initials}
                  </div>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* User Info */}
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <div className="flex items-center gap-2">
                      {user.displayName && (
                        <p className="text-sm font-medium">{user.displayName}</p>
                      )}
                      {isPro && <ProBadge />}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                {isPro ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        (async () => {
                          try {
                            const token = await auth.currentUser?.getIdToken();
                            if (!token) return;
                            const res = await fetch("/api/stripe-portal", {
                              method: "POST",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                            });
                            const data = await res.json();
                            if (data.url) window.location.href = data.url;
                          } catch (err) {
                            console.error("Failed to open portal:", err);
                          }
                        })();
                      }}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Manage Subscription
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => openUpgradePrompt()} className="text-amber-600 focus:text-amber-600">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Upgrade to Pro
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />

                {/* Stats */}
                <DropdownMenuItem onSelect={() => window.location.href = "/stats"}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Statistics
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                {/* Native Language */}
                {/* TODO: Russian support hidden — re-enable by adding "ru" radio item back */}

                {/* Default Translation Direction */}
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                  Default Translation
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={preferences.defaultTranslationDirection}
                  onValueChange={(v) => setDefaultTranslationDirection(v as any)}
                >
                  {Object.entries(DIRECTION_LABELS).map(([value, label]) => (
                    <DropdownMenuRadioItem key={value} value={value}>
                      {label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />

                {/* Auto Categories */}
                <DropdownMenuCheckboxItem
                  checked={preferences.autoCategories}
                  onCheckedChange={(checked) => setAutoCategories(!!checked)}
                >
                  Auto-categorize words
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />

                {/* My Gender */}
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                  My Gender
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={preferences.gender || ""}
                  onValueChange={(v) => setGender(v as "male" | "female")}
                >
                  <DropdownMenuRadioItem value="male">Male</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="female">Female</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />

                {/* Replay Tutorial */}
                <DropdownMenuItem onClick={() => setWalkthroughCompleted(false)}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Replay Tutorial
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                {/* Sign Out */}
                <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Mobile Navigation */}
        <nav className="md:hidden flex items-center gap-1 pb-2 overflow-x-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap",
                  isActive
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:bg-secondary/50"
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}
